import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SupportTrigger } from "@/components/SupportTrigger";
import { faqGroups, allFaqs, faqJsonLd } from "@/lib/faq-content";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ – Häufige Fragen zu SalonTurbo" },
      {
        name: "description",
        content:
          "Antworten zu Registrierung, Boost-Tagen, Coins, Rechnung und Google Ads: alles, was Friseursalons vor dem Start von SalonTurbo wissen wollen.",
      },
      { property: "og:title", content: "FAQ – Häufige Fragen zu SalonTurbo" },
      {
        property: "og:description",
        content:
          "Registrierung, Boost-Kalender, Coins und Google Ads – die häufigsten Fragen von Friseursalons, kurz beantwortet.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqJsonLd(allFaqs)) }],
  }),
  component: FaqPage,
});

function FaqPage() {
  const [openKey, setOpenKey] = useState<string | null>(allFaqs[0]?.question ?? null);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <section className="border-b bg-secondary/40">
          <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:py-20">
            <p className="eyebrow text-muted-foreground">Fragen & Antworten</p>
            <h1 className="mt-4 font-display text-3xl font-light sm:text-5xl">
              Alles, was du vor dem Start wissen willst
            </h1>
            <div className="mx-auto mt-6 h-px w-16 bg-primary" />
            <p className="mx-auto mt-6 max-w-2xl text-muted-foreground">
              Kurz und ohne Fachbegriffe. Wenn deine Frage fehlt, schreib uns – wir antworten in der
              Regel am nächsten Werktag.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {faqGroups.map((group) => (
                <a
                  key={group.id}
                  href={`#${group.id}`}
                  className="rounded-full border bg-background px-4 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {group.title}
                </a>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-3xl px-4 py-14">
          {faqGroups.map((group) => (
            <section key={group.id} id={group.id} className="scroll-mt-24 pb-12">
              <h2 className="font-display text-2xl font-normal sm:text-3xl">{group.title}</h2>
              {group.intro && (
                <p className="mt-2 text-sm text-muted-foreground">{group.intro}</p>
              )}
              <div className="mt-6 space-y-3">
                {group.items.map((item) => {
                  const isOpen = openKey === item.question;
                  return (
                    <div
                      key={item.question}
                      className={`rounded-2xl border bg-card transition-shadow ${isOpen ? "shadow-soft" : ""}`}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenKey(isOpen ? null : item.question)}
                        aria-expanded={isOpen}
                        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                      >
                        <span className="font-display text-base font-medium sm:text-lg">
                          {item.question}
                        </span>
                        <ChevronDown
                          className={`size-5 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {isOpen && (
                        <div className="px-5 pb-5">
                          <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                            {item.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <section className="bg-espresso text-espresso-foreground">
          <div className="mx-auto flex max-w-4xl flex-col items-start gap-6 px-4 py-16 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-display text-2xl font-light sm:text-3xl">
                Frage nicht beantwortet?
              </h2>
              <p className="mt-3 text-sm text-espresso-foreground/70">
                Schreib uns kurz – oder leg direkt los und richte deinen Salon ein.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <SupportTrigger variant="secondary" size="lg" label="Support kontaktieren" />
              <Button asChild size="lg">
                <Link to="/auth">
                  Jetzt starten <ArrowRight className="ml-1 size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
